<template>
  <div>
    <v-dialog
      scrollable
      v-model="dialogs.unrevocable"
      persistent
      max-width="400"
    >
      <material-card
        :title="$t('views.beneficiaries.confirm_unrevocable_edit')"
        :color="$vuetify.theme.themes.light.warning"
        icon="warning"
      >
        <v-card-text>
          {{ $t("views.beneficiaries.unrevocable_beneficiaries_warning") }}
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="dialogs.unrevocable = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn
            :loading="saving"
            color="primary"
            text
            @click="$emit('submit-beneficiaries')"
          >
            {{ $t("globals.continue") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="dialogs.delete" persistent max-width="400">
      <material-card
        v-if="editedItem"
        :title="
          $t('views.beneficiaries.delete_beneficiary_{0}', [
            trans(editedItem, 'LastName') +
              ' ' +
              trans(editedItem, 'FirstName'),
          ])
        "
        :color="$vuetify.theme.themes.light.error"
        icon="delete"
      >
        <v-card-text>{{ $t("globals.are_you_sure") }}</v-card-text>
        <v-card-actions>
          <v-btn color text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="tryDelete">
            {{ $t("globals.delete") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog
      scrollable
      v-if="beneficiaryPayload && beneficiaryPayload != null"
      v-model="dialogs.deleteStatement"
      persistent
      max-width="400"
    >
      <material-card
        :title="$t('views.beneficiaries.delete_beneficiary_statement')"
        :color="$vuetify.theme.themes.light.error"
        icon="delete"
      >
        <v-card-text>{{ $t("globals.are_you_sure") }}</v-card-text>
        <v-card-actions>
          <v-btn color text @click="dialogs.deleteStatement = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="tryDeleteStatement">
            {{ $t("globals.delete") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="dialogs.edit" persistent max-width="1000px">
      <material-card
        v-if="editedItem"
        :title="
          isNew
            ? $t('views.beneficiaries.create_new_beneficiary')
            : $t('views.beneficiaries.edit_beneficiary_{0}', [
                trans(editedItem, 'LastName') +
                  ' ' +
                  trans(editedItem, 'FirstName'),
              ])
        "
        :color="isNew ? $vuetify.theme.themes.light.success : $vuetify.theme.themes.light.primary"
        :icon="isNew ? 'edit' : 'add'"
      >
        <v-card-text>
          <v-form ref="beneficiaryEditForm" v-model="valid">
            <v-container fluid>
              <v-row>
                <v-col
                  xs="12"
                  sm="6"
                  md="4"
                  lg="4"
                  v-if="selectedPlan.PlanType === 'Pension'"
                >
                  <no-autocomplete-textfield
                    :disabled="planIdentity.AutogenerateCertificate"
                    :label="$t('views.beneficiaries.pension_beneficiary_id')"
                    v-model="editedItem.PensionBeneficiaryId"
                    :rules="[$rules.pensionBeneficiaryId, $rules.required]"
                    class="required"
                  ></no-autocomplete-textfield>
                </v-col>
                <v-col xs="12" sm="6" md="4" lg="4">
                  <trans-select
                    :item="editedItem"
                    propertyName="BeneficiaryType"
                    :items="beneficiaryTypes"
                    :label="$t('globals.type')"
                    :rules="[$rules.required]"
                    class="required"
                  ></trans-select>
                </v-col>
                <template v-if="beneficiaryIsPerson(editedItem)">
                  <v-col xs="12" sm="6" md="4" lg="4">
                    <trans-select
                      :item="editedItem"
                      propertyName="BeneficiaryRelationship"
                      :items="beneficiaryRelationships"
                      :label="
                        $t('components.inmemorydependants.relationship')
                      "
                      :rules="[$rules.required]"
                      class="required"
                      @change="onRelationship"
                    ></trans-select>
                  </v-col>
                  <template v-if="showDependants">
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <auto-select
                        v-if="dependants !== null && dependants !== undefined"
                        :value.sync="editedItem.DependantIdentity"
                        :items="filteredDependants"
                        :label="
                          selectedPlan.PlanType === 'Pension'
                            ? $t('globals.spouse')
                            : $t('globals.dependants')
                        "
                        item-value="Identifier"
                        :item-text="
                          (item) => item.FirstName + ' ' + item.LastName
                        "
                        :clearable="true"
                        @add="
                          addNewDependant = true;
                          showAddDependantBtn = false;
                        "
                        :showAddBtn="showAddDependantBtn"
                      ></auto-select>
                      <auto-select
                        v-else-if="
                          dependantIdentities !== null &&
                            dependantIdentities !== undefined
                        "
                        :value.sync="editedItem.DependantIdentity"
                        :items="filteredDependantIdentities"
                        :label="
                          selectedPlan.PlanType === 'Pension'
                            ? $t('globals.spouse')
                            : $t('globals.dependants')
                        "
                        item-value="Id"
                        :item-text="
                          (item) =>
                            item.Current.FirstName + ' ' + item.Current.LastName
                        "
                        :clearable="true"
                        @add="
                          addNewDependant = true;
                          showAddDependantBtn = false;
                        "
                        :showAddBtn="showAddDependantBtn"
                        @change="onDependant"
                      ></auto-select>
                    </v-col>
                    <template v-if="addNewDependant">
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <no-autocomplete-textfield
                          :label="$t('headers.members_management.SIN')"
                          v-model="editedItem.SIN"
                          :rules="[$rules.sin]"
                        ></no-autocomplete-textfield>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <trans-select
                          :clearable="true"
                          :item="editedItem"
                          propertyName="Title"
                          :items="columnsDropDownOptions.Models.Title"
                          :label="$t('headers.members_management.Title')"
                        ></trans-select>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <no-autocomplete-textfield
                          v-model="editedItem.FirstName"
                          :label="$t(nameField(editedItem))"
                          :rules="[$rules.required, $rules.validName]"
                          class="required"
                        ></no-autocomplete-textfield>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <no-autocomplete-textfield
                          v-model="editedItem.MiddleName"
                          :label="$t('globals.middlename')"
                          :rules="[$rules.validMiddleName]"
                        ></no-autocomplete-textfield>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <no-autocomplete-textfield
                          v-model="editedItem.LastName"
                          :label="$t('globals.lastname')"
                          :rules="[$rules.required, $rules.validLastName]"
                          class="required"
                        ></no-autocomplete-textfield>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <trans-select
                          :clearable="true"
                          :item="editedItem"
                          propertyName="Suffix"
                          :items="columnsDropDownOptions.Models.Suffix"
                          :label="$t('headers.members_management.Suffix')"
                        ></trans-select>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <pbas-date-picker
                          :label="$t('globals.dob')"
                          :value="editedItem.DateOfBirth"
                          @update-date="editedItem.DateOfBirth = $event"
                          :rules="[$rules.dateOfBirth]"
                          :allowedDates="dateOfBirthDates"
                        ></pbas-date-picker>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <pbas-date-picker
                          :label="$t('components.inmemorydependants.dod')"
                          :value="editedItem.DateOfDeath"
                          @update-date="editedItem.DateOfDeath = $event"
                          :rules="[$rules.dateOfBirth]"
                          :allowedDates="dateOfBirthDates"
                        ></pbas-date-picker>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4">
                        <trans-select
                          :item="editedItem"
                          propertyName="Gender"
                          :items="genders"
                          :label="$t('globals.gender')"
                        ></trans-select>
                      </v-col>
                    </template>
                  </template>
                  <template v-else>
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <trans-select
                        :clearable="true"
                        :item="editedItem"
                        propertyName="Title"
                        :items="columnsDropDownOptions.Models.Title"
                        :label="$t('headers.members_management.Title')"
                      ></trans-select>
                    </v-col>
                    <v-col
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                      v-show="editedItem.BeneficiaryRelationship"
                    >
                      <no-autocomplete-textfield
                        v-model="editedItem.FirstName"
                        :label="$t(nameField(editedItem))"
                        :rules="[$rules.required, $rules.validName]"
                        class="required"
                      ></no-autocomplete-textfield>
                    </v-col>
                    <v-col
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                      v-show="editedItem.BeneficiaryRelationship"
                    >
                      <no-autocomplete-textfield
                        v-model="editedItem.MiddleName"
                        :label="$t('globals.middlename')"
                        :rules="[$rules.validMiddleName]"
                      ></no-autocomplete-textfield>
                    </v-col>
                    <v-col
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                      v-show="editedItem.BeneficiaryRelationship"
                    >
                      <no-autocomplete-textfield
                        v-model="editedItem.LastName"
                        :label="$t('globals.lastname')"
                        :rules="[$rules.required, $rules.validLastName]"
                        class="required"
                      ></no-autocomplete-textfield>
                    </v-col>
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <trans-select
                        :clearable="true"
                        :item="editedItem"
                        propertyName="Suffix"
                        :items="columnsDropDownOptions.Models.Suffix"
                        :label="$t('headers.members_management.Suffix')"
                      ></trans-select>
                    </v-col>
                    <v-col
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                      v-show="editedItem.BeneficiaryRelationship"
                    >
                      <pbas-date-picker
                        :label="$t('globals.dob')"
                        :value="editedItem.DateOfBirth"
                        @update-date="editedItem.DateOfBirth = $event"
                        :rules="[$rules.dateOfBirth]"
                        :allowedDates="dateOfBirthDates"
                      ></pbas-date-picker>
                    </v-col>
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <pbas-date-picker
                        :label="$t('components.inmemorydependants.dod')"
                        :value="editedItem.DateOfDeath"
                        @update-date="editedItem.DateOfDeath = $event"
                        :rules="[$rules.dateOfBirth]"
                        :allowedDates="dateOfBirthDates"
                      ></pbas-date-picker>
                    </v-col>
                    <v-col
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                      v-show="editedItem.BeneficiaryRelationship"
                    >
                      <trans-select
                        :item="editedItem"
                        propertyName="Gender"
                        :items="genders"
                        :label="$t('globals.gender')"
                      ></trans-select>
                    </v-col>
                  </template>
                </template>
                <v-col
                  xs="12"
                  sm="6"
                  md="4"
                  lg="4"
                  v-else-if="editedItem.BeneficiaryType"
                >
                  <no-autocomplete-textfield
                    v-model="editedItem.FirstName"
                    :label="$t(nameField(editedItem))"
                    :rules="[$rules.required, $rules.validName]"
                    class="required"
                  ></no-autocomplete-textfield>
                </v-col>
              </v-row>
              <v-row v-if="isBeneficiary">
                <v-col xs="12" sm="6" md="4" lg="4">
                  <v-checkbox
                    v-model="editedItem.IsPrimary"
                    :label="$t('components.plancreationwizard.primary')"
                  ></v-checkbox>
                </v-col>
                <v-col xs="12" sm="6" md="4" lg="4">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-container pa-0>
                        <v-row no-gutters>
                          <v-col cols="auto">
                            <v-checkbox
                              v-model="editedItem.IsRevocable"
                              :label="$t('views.beneficiaries.revocable')"
                            ></v-checkbox>
                          </v-col>
                          <v-col
                            style="margin-top: 20px; margin-left: 3px"
                            cols="auto"
                          >
                            <v-icon v-bind="attrs" v-on="on">help</v-icon>
                          </v-col>
                        </v-row>
                      </v-container>
                    </template>
                    <span>
                      {{ $t("views.beneficiaries.revocable_tooltip") }}
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col xs="12" sm="6" md="4" lg="4">
                  <currency-textfield
                    v-model="editedItem.Percentage"
                    :label="$t('views.beneficiaries.percentage')"
                    class="required"
                    :rules="[$rules.requiredNum, $rules.dynamic.maxValue(100)]"
                    prefix="%"
                  />
                  <!-- <no-autocomplete-textfield
                    v-model="editedItem.Percentage"
                    :label="$t('views.beneficiaries.percentage')"
                    @keydown="currencyKeydown"
                    @keyup="currencyKeyUp"
                    @blur="rateBlur"
                    :rules="[$rules.required, $rules.dynamic.maxValue(100)]"
                    class="required"
                    prefix="%"
                  ></no-autocomplete-textfield> -->
                </v-col>
                <v-col
                  v-if="beneficiaryIsPerson(editedItem)"
                  xs="12"
                  sm="6"
                  md="4"
                  lg="4"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-container pa-0>
                        <v-row no-gutters>
                          <v-col cols="auto">
                            <v-checkbox
                              v-model="editedItem.AddTrustee"
                              :label="$t('views.beneficiaries.add_trustee')"
                            ></v-checkbox>
                          </v-col>
                          <v-col
                            style="margin-top: 20px; margin-left: 3px"
                            cols="auto"
                          >
                            <v-icon v-bind="attrs" v-on="on">help</v-icon>
                          </v-col>
                        </v-row>
                      </v-container>
                    </template>
                    <span>
                      {{ $t("views.beneficiaries.add_trustee_tooltip") }}
                    </span>
                  </v-tooltip>
                </v-col>
              </v-row>
              <template v-if="editedItem.AddTrustee">
                <!-- <v-form ref="trusteeEditForm" v-model="validTrustees"> -->
                <!-- <v-card
                  v-for="beneficiary in underageBeneficiaries()"
                  v-bind:key="beneficiary['UniqueIdentifier']"
                > -->
                <!-- <v-card-title>
                    {{
                      $t("views.beneficiaries.trustee_for_{0}", [
                        editedItem.FirstName +
                          " " +
                          (editedItem.LastName ? editedItem.LastName : ""),
                      ])
                    }}
                  </v-card-title> -->
                <!-- <v-card-text> -->
                <v-row>
                  <v-col
                    v-if="copyFromBeneficiaries(editedItem).length > 0"
                    xs="12"
                    sm="6"
                    md="4"
                    lg="4"
                  >
                    <auto-select
                      class="required"
                      :label="
                        $t('views.beneficiaries.same_trustee_as_beneficiary')
                      "
                      :value.sync="copyFrom[editedItem.UniqueIdentifier]"
                      :items="copyFromBeneficiaries(editedItem)"
                      item-text="FirstName"
                      item-value="UniqueIdentifier"
                      return-object
                    ></auto-select>
                  </v-col>
                  <v-col xs="12" sm="6" md="4" lg="4">
                    <v-btn
                      v-if="
                        copyFrom[editedItem.UniqueIdentifier] &&
                          copyFrom[editedItem.UniqueIdentifier] != null
                      "
                      :loading="saving"
                      color="primary"
                      @click="copyTrusteeFrom(editedItem)"
                    >
                      {{ $t("globals.copy") }}
                    </v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col xs="12" sm="6" md="4" lg="4">
                    <trans-select
                      :item="editedItem.Trustee"
                      propertyName="BeneficiaryType"
                      :items="beneficiaryTypes"
                      :label="$t('globals.type')"
                      :rules="[$rules.required]"
                      class="required"
                      @change="trusteeTypeChanged(editedItem.Trustee)"
                    ></trans-select>
                  </v-col>
                  <template
                    v-if="
                      editedItem.Trustee.BeneficiaryType !== null &&
                        editedItem.Trustee.BeneficiaryType !== undefined
                    "
                  >
                    <v-col
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                      v-if="beneficiaryIsPerson(editedItem.Trustee)"
                    >
                      <no-autocomplete-textfield
                        v-model="editedItem.Trustee.FirstName"
                        :label="$t('globals.firstname')"
                        :rules="[$rules.required, $rules.validName]"
                        class="required"
                      ></no-autocomplete-textfield>
                    </v-col>
                    <v-col xs="12" sm="6" md="4" lg="4" v-else>
                      <no-autocomplete-textfield
                        v-model="editedItem.Trustee.FirstName"
                        :label="$t('globals.name')"
                        :rules="[$rules.required, $rules.validName]"
                        class="required"
                      ></no-autocomplete-textfield>
                    </v-col>
                    <v-col
                      v-if="beneficiaryIsPerson(editedItem.Trustee)"
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                    >
                      <no-autocomplete-textfield
                        v-model="editedItem.Trustee.MiddleName"
                        :label="$t('globals.middlename')"
                        :rules="[$rules.validMiddleName]"
                      ></no-autocomplete-textfield>
                    </v-col>
                    <v-col
                      v-if="beneficiaryIsPerson(editedItem.Trustee)"
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                    >
                      <no-autocomplete-textfield
                        v-model="editedItem.Trustee.LastName"
                        :label="$t('globals.lastname')"
                        :rules="[$rules.required, $rules.validLastName]"
                        class="required"
                      ></no-autocomplete-textfield>
                    </v-col>
                  </template>
                </v-row>
                <v-row>
                  <v-col
                    v-if="beneficiaryIsPerson(editedItem.Trustee)"
                    xs="12"
                    sm="6"
                    md="4"
                    lg="4"
                  >
                    <trans-select
                      :item="editedItem.Trustee"
                      propertyName="BeneficiaryRelationship"
                      :items="beneficiaryRelationships"
                      :label="$t('views.beneficiaries.trustee_relationship')"
                      :rules="[$rules.required]"
                      class="required"
                    ></trans-select>
                  </v-col>
                </v-row>
                <!-- </v-card-text> -->
                <!-- </v-card> -->
                <!-- </v-form> -->
              </template>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="closeEdit">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            text
            :disabled="!valid"
            @click="trySaveBeneficiary"
          >
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <div
      v-if="
        !newStatement &&
          beneficiaryStatements &&
          beneficiaryStatements.length > 0
      "
    >
      <v-row>
        <v-col xs="12" sm="6" md="6" lg="6">
          <v-autocomplete
            :rules="[$rules.required]"
            class="required"
            :label="$t('views.beneficiaries.beneficiary_statements')"
            item-value="id"
            item-text="CurrentPolicies"
            v-model="selectedBeneficiaryStatement"
            :items="beneficiaryStatements"
            return-object
            :name="randomName"
            :id="randomName"
            :autocomplete="randomName"
          ></v-autocomplete>
        </v-col>
        <v-col
          v-if="beneficiaryPayload && beneficiaryPayload != null"
          xs="12"
          sm="6"
          md="6"
          lg="6"
        >
          <v-btn color="error" dark @click="dialogs.deleteStatement = true">
            <v-icon>delete</v-icon>
            {{ $t("globals.delete") }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <div v-if="beneficiaryPayload && beneficiaryPayload != null">
      <v-card>
        <v-card-title>
          {{ $t("globals.beneficiaries") }}
        </v-card-title>
        <v-card-text>
          <v-form v-model="rootValid">
            <v-row>
              <v-col
                xs="12"
                sm="6"
                md="6"
                lg="6"
                v-if="selectedPlan.PlanType !== 'Pension'"
              >
                <v-autocomplete
                  :rules="[$rules.atLeastOne]"
                  class="required"
                  multiple
                  :label="$t('views.beneficiaries.policies')"
                  item-value="Id"
                  item-text="Name"
                  v-model="beneficiaryPayload.PolicyIdentities"
                  :items="innerPolicyIdentities"
                  return-object
                ></v-autocomplete>
              </v-col>
              <v-col
                v-if="previousDeclarationDate != null"
                xs="12"
                sm="6"
                md="6"
                lg="6"
              >
                <pbas-date-picker
                  style="width: 100%"
                  :label="$t('views.beneficiaries.declaration_date')"
                  :value="beneficiaryPayload.DeclarationDate"
                  :allowedDates="
                    allowedBeneficiaryDates.bind(this, previousDeclarationDate)
                  "
                  :rules="[$rules.required]"
                  class="required"
                  @update-date="declarationDateChanged($event)"
                ></pbas-date-picker>
              </v-col>
              <v-col
                xs="12"
                sm="12"
                md="12"
                lg="12"
                v-show="newMember || !compareDates"
                v-if="useDocumentsManagement"
              >
                <multi-file-upload2
                  id="declarationId"
                  @emit-upload-end="$emit('file-upload-end')"
                  :files="files"
                  :filesIds.sync="filesIds"
                  @emit-upload-start="$emit('file-upload-start')"
                  :label="$t('views.beneficiaries.Add Declaration Form')"
                  :tags="[selectedPlan.UniqueIdentifier]"
                  :categories="['BeneficiariesDeclaration']"
                  :currentFolder="selectedPlan.Name"
                  :zipDownloadUrl="
                    settings.DOCUMENTSERVICEURL + 'api/ZipFilesDownload'
                  "
                  :fileUploadUrl="
                    settings.DOCUMENTSERVICEURL + 'api/UploadFiles'
                  "
                  @emit-files="
                    bindDeclarationForm($event);
                    $emit('file-upload-end');
                  "
                  @emit-upload-error="
                    error = $event;
                    $emit('file-upload-end');
                  "
                  :getFilesUrl="settings.DOCUMENTSERVICEURL + 'api/Files'"
                />
              </v-col>
              <v-col xs="12" sm="12" md="12" lg="12" v-show="compareDates" v-if="useDocumentsManagement">
                <multi-file-upload2
                  id="declaration2Id"
                  @emit-upload-end="$emit('file-upload-end')"
                  :label="$t('views.beneficiaries.View Declaration Form')"
                  v-if="!newMember"
                  :filesIds.sync="filesIds"
                  :disabled="true"
                  :tags="[selectedPlan.UniqueIdentifier]"
                  :categories="['BeneficiariesDeclaration']"
                  :currentFolder="selectedPlan.Name"
                  :zipDownloadUrl="
                    settings.DOCUMENTSERVICEURL + 'api/ZipFilesDownload'
                  "
                  :getFilesUrl="settings.DOCUMENTSERVICEURL + 'api/Files'"
                  :fileUploadUrl="
                    settings.DOCUMENTSERVICEURL + 'api/UploadFiles'
                  "
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <pbas-data-table
                  ref="beneficiaryTable"
                  :showSelect="false"
                  :dataProvider="provider"
                  :rowActions="rowActions"
                  :floatActions="floatActions"
                  @itemsChanged="setData($event)"
                  @error="error = $event"
                ></pbas-data-table>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
      <!-- <v-card v-if="underageBeneficiaries().length > 0">
        <v-card-title>
          {{ $t("views.beneficiaries.trustees") }}
        </v-card-title>
        <v-card-text>
          <v-container fluid>
            <v-form ref="trusteeEditForm" v-model="validTrustees">
              <v-card
                v-for="beneficiary in underageBeneficiaries()"
                v-bind:key="beneficiary['UniqueIdentifier']"
              >
                <v-card-title>
                  {{
                    $t("views.beneficiaries.trustee_for_{0}", [
                      beneficiary.FirstName +
                        " " +
                        (beneficiary.LastName ? beneficiary.LastName : ""),
                    ])
                  }}
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col
                      v-if="copyFromBeneficiaries(beneficiary).length > 0"
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                    >
                      <auto-select
                        class="required"
                        :label="
                          $t('views.beneficiaries.same_trustee_as_beneficiary')
                        "
                        :value.sync="copyFrom[beneficiary.UniqueIdentifier]"
                        :items="copyFromBeneficiaries(beneficiary)"
                        item-text="FirstName"
                        item-value="UniqueIdentifier"
                        return-object
                      ></auto-select>
                    </v-col>
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <v-btn
                        v-if="
                          copyFrom[beneficiary.UniqueIdentifier] &&
                            copyFrom[beneficiary.UniqueIdentifier] != null
                        "
                        :loading="saving"
                        color="primary"
                        @click="copyTrusteeFrom(beneficiary)"
                      >
                        {{ $t("globals.copy") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <trans-select
                        :item="beneficiary.Trustee"
                        propertyName="BeneficiaryType"
                        :items="beneficiaryTypes"
                        :label="$t('globals.type')"
                        :rules="[$rules.required]"
                        class="required"
                        @change="trusteeTypeChanged(beneficiary.Trustee)"
                      ></trans-select>
                    </v-col>
                    <template
                      v-if="
                        beneficiary.Trustee.BeneficiaryType !== null &&
                          beneficiary.Trustee.BeneficiaryType !== undefined
                      "
                    >
                      <v-col
                        xs="12"
                        sm="6"
                        md="4"
                        lg="4"
                        v-if="beneficiaryIsPerson(beneficiary.Trustee)"
                      >
                        <no-autocomplete-textfield
                          v-model="beneficiary.Trustee.FirstName"
                          :label="$t('globals.firstname')"
                          :rules="[$rules.required, $rules.validName]"
                          class="required"
                        ></no-autocomplete-textfield>
                      </v-col>
                      <v-col xs="12" sm="6" md="4" lg="4" v-else>
                        <no-autocomplete-textfield
                          v-model="beneficiary.Trustee.FirstName"
                          :label="$t('globals.name')"
                          :rules="[$rules.required, $rules.validName]"
                          class="required"
                        ></no-autocomplete-textfield>
                      </v-col>
                      <v-col
                        v-if="beneficiaryIsPerson(beneficiary.Trustee)"
                        xs="12"
                        sm="6"
                        md="4"
                        lg="4"
                      >
                        <no-autocomplete-textfield
                          v-model="beneficiary.Trustee.MiddleName"
                          :label="$t('globals.middlename')"
                          :rules="[$rules.validMiddleName]"
                        ></no-autocomplete-textfield>
                      </v-col>
                      <v-col
                        v-if="beneficiaryIsPerson(beneficiary.Trustee)"
                        xs="12"
                        sm="6"
                        md="4"
                        lg="4"
                      >
                        <no-autocomplete-textfield
                          v-model="beneficiary.Trustee.LastName"
                          :label="$t('globals.lastname')"
                          :rules="[$rules.required, $rules.validLastName]"
                          class="required"
                        ></no-autocomplete-textfield>
                      </v-col>
                    </template>
                  </v-row>
                  <v-row>
                    <v-col
                      v-if="beneficiaryIsPerson(beneficiary.Trustee)"
                      xs="12"
                      sm="6"
                      md="4"
                      lg="4"
                    >
                      <trans-select
                        :item="beneficiary.Trustee"
                        propertyName="BeneficiaryRelationship"
                        :items="beneficiaryRelationships"
                        :label="$t('views.beneficiaries.trustee_relationship')"
                        :rules="[$rules.required]"
                        class="required"
                      ></trans-select>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-form>
          </v-container>
        </v-card-text>
      </v-card> -->
      <error-handler :error="error"></error-handler>
      <!-- <v-card-actions v-if="previousDeclarationDate != null">
        <v-btn
          :loading="saving"
          :disabled="!saveEnabled()"
          color="primary"
          @click="trySubmitBeneficiaries"
        >
      </v-card-actions> -->
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { InMemoryProvider } from "pbas-vue-components";
import getInMemoryBeneficiariesHeaders from "../headers/Beneficiaries";
import ErrorHandler from "../components/ErrorHandler";
import TransSelect from "../components/TransSelect";
import TransTextbox from "../components/TransTextbox";
import { translate2 } from "../trans2";
import randomID from "../utils/randomID";
import TrustFundVue from "../views/TrustFund.vue";
import AutoSelect from "../components/AutoSelect";
import NoAutocompleteTextfield from "../components/NoAutocompleteTextfield";
import { createMemberFile } from "../makeMemberFile";
import MultiFileUpload2 from "../components/MultiFileUpload2.vue";
import CurrencyTextfield from "../components/CurrencyTextfield.vue";
import TransSelect2 from "../components/TransSelect2";
import SettingsService from "@/services/settings.service.js";

export default {
  name: "beneficiaries-form",
  components: {
    ErrorHandler,
    TransSelect,
    TransTextbox,
    AutoSelect,
    NoAutocompleteTextfield,
    MultiFileUpload2,
    CurrencyTextfield,
    TransSelect2,
  },
  props: [
    "newStatement",
    "memberIdentityId",
    "policyIdentities",
    "disabled",
    "previousBeneficiaryPayload",
    "beneficiaryStatements",
    "selectedStatement",
    "newMember",
    "files",
    "dependants",
    "dependantIdentities",
  ],
  data() {
    return {
      filesIds: [],
      compareDates: false,
      copyFrom: {},
      randomName: null,
      rootValid: true,
      provider: null,
      dialogs: {
        unrevocable: false,
        delete: false,
        deleteStatement: false,
        edit: false,
      },
      innerPolicyIdentities: [],
      previousDeclarationDate: null,
      isNew: false,
      beneficiaryPayload: null,
      selectedBeneficiaryStatement: null,
      error: null,
      saving: false,
      isBeneficiary: true,
      valid: true,
      validTrustees: true,
      editedItem: null,
      genders: [],
      beneficiaryRelationships: [],
      beneficiaryTypes: [],
      rowActions: [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          visible: () => true,
          handler: (item) => {
            this.loading = true;
            this.editedItem = JSON.parse(JSON.stringify(item));
            this.isNew = false;
            this.isBeneficiary = true;
            console.log("edit bene: ", this.showDependantsFunc());
            if (this.showDependantsFunc()) {
              this.showAddDependantBtn = false;
              if (this.editedItem.DependantIdentity) {
                this.addNewDependant = false;
              } else {
                this.addNewDependant = true;
              }
            }
            this.dialogs.edit = true;
            console.log(this.editedItem);
            this.$nextTick(() => {
              this.loading = false;
            });
          },
        },
        {
          name: "delete",
          icon: "delete",
          title: "globals.delete",
          visible: () => true,
          handler: (item) => {
            this.editedItem = JSON.parse(JSON.stringify(item));
            this.dialogs.delete = true;
          },
        },
      ],
      floatActions: [
        {
          text: "globals.add_new",
          color: this.$vuetify.theme.themes.light.success,
          name: "add",
          icon: "add",
          visible: () => true,
          handler: async () => {
            this.loading = true;
            this.editedItem = {
              UniqueIdentifier: randomID(20),
            };
            if (this.planIdentity.AutogenerateCertificate) {
              if (this.selectedPlan.PlanType === "Pension") {
                this.editedItem.PensionBeneficiaryId = (await this.getNewCertificate(
                  {
                    planIdentityId: this.selectedPlan.Id,
                    entity: "ContingentBeneficiary",
                  }
                )).data.value;
              }
            }
            this.isBeneficiary = true;
            this.isNew = true;
            this.dialogs.edit = true;
            if (this.$refs.beneficiaryEditForm) {
              this.$refs.beneficiaryEditForm.resetValidation();
            }
            this.showDependants = false;
            this.showAddDependantBtn = true;
            this.$nextTick(() => {
              this.loading = false;
            });
          },
        },
      ],
      addNewDependant: false,
      showAddDependantBtn: true,
      loading: false,
      showDependants: false,
    };
  },
  computed: {
    ...mapGetters("plansMembers", {
      columnsDropDownOptions: "options",
      planParameters: "planOptions",
    }),
    ...mapGetters("common", ["language", "settings"]),
    ...mapGetters("common", { selectedPlan: "plan" }),
    ...mapGetters("plansMembers", ["planIdentity"]),
    useDocumentsManagement() {
      var settings = SettingsService.settings;
      // console.log("useDocumentsManagement", settings.UseDocumentsManagement);
      return settings.UseDocumentsManagement;
    },
    filteredDependants() {
      if (this.editedItem === null || this.editedItem === undefined) {
        return [];
      }
      if (
        this.editedItem.BeneficiaryRelationship === null ||
        this.editedItem.BeneficiaryRelationship === undefined
      ) {
        return [];
      }
      if (this.editedItem.BeneficiaryRelationship.IsSpouse === true) {
        return this.dependants.filter((o) => o.Relationship.IsSpouse === true);
      }
      if (this.editedItem.BeneficiaryRelationship.IsChild === true) {
        return this.dependants.filter((o) => o.Relationship.IsChild === true);
      }
      return this.dependants;
    },
    filteredDependantIdentities() {
      if (this.editedItem === null || this.editedItem === undefined) {
        return [];
      }
      if (
        this.editedItem.BeneficiaryRelationship === null ||
        this.editedItem.BeneficiaryRelationship === undefined
      ) {
        return [];
      }
      if (this.editedItem.BeneficiaryRelationship.IsSpouse === true) {
        return this.dependantIdentities.filter(
          (o) => o.Current.Relationship.IsSpouse === true
        );
      }
      if (this.editedItem.BeneficiaryRelationship.IsChild === true) {
        return this.dependantIdentities.filter(
          (o) => o.Current.Relationship.IsChild === true
        );
      }
      return this.dependantIdentities;
    },
  },
  watch: {
    "editedItem.IsPrimary": {
      async handler(v) {
        if (this.loading) {
          return;
        }
        if (v) {
          if (this.planIdentity.AutogenerateCertificate) {
            if (this.selectedPlan.PlanType === "Pension") {
              this.editedItem.PensionBeneficiaryId = (await this.getNewCertificate(
                {
                  planIdentityId: this.selectedPlan.Id,
                  entity: "PrimaryBeneficiary",
                }
              )).data.value;
            }
          }
        } else {
          if (this.planIdentity.AutogenerateCertificate) {
            if (this.selectedPlan.PlanType === "Pension") {
              this.editedItem.PensionBeneficiaryId = (await this.getNewCertificate(
                {
                  planIdentityId: this.selectedPlan.Id,
                  entity: "ContingentBeneficiary",
                }
              )).data.value;
            }
          }
        }
      },
    },
    "editedItem.AddTrustee": {
      handler(v) {
        if (v) {
          if (this.editedItem.Trustee === null || this.editedItem.Trustee === undefined) {
            this.editedItem.Trustee = {};
          } 
        } else {
          delete this.editedItem.Trustee;
        }
      },
    },
    "beneficiaryPayload.Beneficiaries": {
      handler() {
        this.$emit("valid", this.saveEnabled());
      },
      immediate: true,
      deep: true,
    },
    "provider.items": {
      handler() {
        const valid = this.saveEnabled();
        console.log(valid);
        this.$emit("valid", valid);
      },
      immediate: true,
      deep: true,
    },
    validTrustees: {
      handler() {
        this.$emit("valid", this.saveEnabled());
      },
      immediate: true,
    },
    previousDeclarationDate: {
      handler() {
        this.$emit("previous-declaration-date", this.previousDeclarationDate);
      },
      immediate: true,
    },
    selectedBeneficiaryStatement: {
      handler: function(v) {
        if (v != null) {
          this.initializeData(v);
        }
      },
    },
    previousBeneficiaryPayload: {
      handler: function(v) {
        if (v != null) {
          this.initializeData(v);
        }
      },
      deep: true,
      immediate: true,
    },
    selectedStatement: {
      handler: function(v) {
        if (v) {
          console.log(v, this.beneficiaryStatements);
          this.selectedBeneficiaryStatement = this.beneficiaryStatements.filter(
            (o) => o.Id === this.selectedStatement.Id
          )[0];
        }
      },
      immediate: true,
    },
    // "editedItem.DependantIdentity": {
    //   handler(v) {

    //   },
    // },
    "editedItem.BeneficiaryType": {
      handler(v, old) {
        if (v !== old) {
          console.log(v, old);
          if (!this.loading) {
            this.addNewDependant = false;
            this.showAddDependantBtn = true;
            if (!this.beneficiaryIsPerson(this.editedItem)) {
              this.editedItem.BeneficiaryRelationship = undefined;
              this.editedItem.DependantIdentity = undefined;
              console.log("------------------------------beneficiaryType");
              this.clearFields();
            }
          }
        }
      },
    },
    // "editedItem.BeneficiaryRelationship": {
    //   handler(v) {
    //     this.onRelationship(v)
    //   },
    // },
    beneficiaryStatements: {
      handler: function(v) {
        if (v && this.selectedPlan.PlanType === "Pension") {
          this.selectedBeneficiaryStatement = this.beneficiaryStatements[0];
        }
      },
      immediate: true,
    },
  },
  mounted() {
    //this.initializeData();
    this.randomName = this.getRandomName();
    console.log(this.policyIdentities);
    if (
      this.beneficiaryStatements &&
      this.selectedPlan.PlanType === "Pension"
    ) {
      this.selectedBeneficiaryStatement = this.beneficiaryStatements[0];
    }
  },
  methods: {
    ...mapActions("plansMembers", [
      "getBeneficiaries",
      "saveBeneficiaries",
      "deleteBeneficiaryStatement",
      "getNewCertificate",
      "releaseCertificate",
    ]),
    ...mapActions("documentsService", ["getFilesById"]),
    closeEdit() {
      if (this.selectedPlan.PlanType === "Pension") {
        this.releaseCertificate({
          planIdentityId: this.selectedPlan.Id,
          payload: {
            Certificates: [this.editedItem.PensionBeneficiaryId],
          },
        });
      }
      this.dialogs.edit = false;
    },
    onDependant(v) {
      console.log(v, this.loading);
      if (!this.loading) {
        this.addNewDependant = false;
        if (v === null || v === undefined) {
          this.showAddDependantBtn = true;
          console.log("------------------------------dependantIdentity");
          this.clearFields();
        } else {
          this.showAddDependantBtn = false;
        }
      }
    },
    onRelationship(v) {
      console.log(
        "BeneficiaryRelationshipvBeneficiaryRelationshipBeneficiaryRelationshipBeneficiaryRelationship",
        v,
        this.showAddDependantBtn,
        this.loading
      );
      if (!this.loading) {
        if (this.showDependantsFunc() && !this.showAddDependantBtn) {
          console.log("------------------------------BeneficiaryRelationship");
          this.clearFields();
        }
        if (!this.showDependantsFunc()) {
          delete this.editedItem.DependantIdentity;
          this.showAddDependantBtn = true;
        }
      }
    },
    clearFields() {
      console.log(
        "!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! clear fields !!!!!!!!!!!!!!!!!!"
      );
      delete this.editedItem.FirstName;
      delete this.editedItem.MiddleName;
      delete this.editedItem.LastName;
      delete this.editedItem.DateOfBirth;
      delete this.editedItem.Gender;
    },
    showDependantsFunc() {
      if (this.editedItem.BeneficiaryRelationship) {
        console.log(
          "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@",
          this.editedItem.BeneficiaryRelationship.IsSpouse,
          this.selectedPlan.PlanType,
          this.addNewDependant
        );
      }
      this.showDependants =
        this.editedItem.BeneficiaryRelationship &&
        (((this.editedItem.BeneficiaryRelationship.IsChild === true ||
          this.editedItem.BeneficiaryRelationship.IsSpouse === true) &&
          this.selectedPlan.PlanType === "HW") ||
          (this.editedItem.BeneficiaryRelationship.IsSpouse === true &&
            this.selectedPlan.PlanType === "Pension"));
      console.log(this.showDependants);
      return this.showDependants;
    },
    // showAddDependantBtn() {
    //   if (this.addNewDependant) {
    //     return false;
    //   }
    //   if (this.editedItem === null || this.editedItem === undefined) {
    //     return false;
    //   }
    //   if (
    //     this.editedItem.BeneficiaryRelationship === null ||
    //     this.editedItem.BeneficiaryRelationship === undefined
    //   ) {
    //     return false;
    //   }
    //   return (
    //     this.editedItem.BeneficiaryRelationship.IsChild ||
    //     this.editedItem.BeneficiaryRelationship.IsSpouse &&
    //     (this.editedItem.DependantIdentity === null ||
    //       this.editedItem.DependantIdentity === undefined)
    //   );
    // },
    declarationDateChanged(v) {
      this.beneficiaryPayload.DeclarationDate = v;
      this.compareDates = this.compareDeclarationDates();
      console.log(this.compareDates);
    },
    bindDeclarationForm(memberFile) {
      this.beneficiaryPayload.DeclarationForm = createMemberFile(
        memberFile,
        "BeneficiariesDeclaration",
        this.memberIdentityId
      );
    },
    compareDeclarationDates() {
      if (this.previousDeclarationDate) {
        const currentDate = this.beneficiaryPayload.DeclarationDate.substring(
          0,
          this.beneficiaryPayload.DeclarationDate.lastIndexOf("T")
        );
        const previousDate = this.previousDeclarationDate.substring(
          0,
          this.previousDeclarationDate.lastIndexOf("T")
        );
        return currentDate === previousDate;
      }
    },
    getRandomName() {
      return Math.random().toString();
    },

    dateOfBirthDates(val) {
      const n = Date.parse(val);
      const d = new Date(n);
      const d2 = new Date();
      return d <= d2;
    },
    copyTrusteeFrom(beneficiary) {
      var oldId = beneficiary.Trustee ? beneficiary.Trustee.Id : null;
      beneficiary.Trustee = JSON.parse(
        JSON.stringify(this.copyFrom[beneficiary.UniqueIdentifier].Trustee)
      );
      if (oldId != null) {
        beneficiary.Trustee.Id = oldId;
      } else {
        delete beneficiary.Trustee.Id;
      }
      this.$forceUpdate();
    },
    trusteeTypeChanged(trustee) {
      console.log("#################")
      this.$nextTick(() => {
        this.$forceUpdate();
        console.log("#################----------------------")
      });
    },
    underageBeneficiaries() {
      if (this.beneficiaryPayload && this.beneficiaryPayload.Beneficiaries) {
        var underage = this.beneficiaryPayload.Beneficiaries.filter(
          (b) => b.AddTrustee
        );
        underage.forEach((u) => {
          if (u.Trustee == null) {
            u.Trustee = {};
          }
        });
        return underage;
      }
      return [];
    },
    copyFromBeneficiaries(beneficiary) {
      var beneficiaries = this.underageBeneficiaries().filter(
        (b) => b.UniqueIdentifier != beneficiary.UniqueIdentifier
      );
      return beneficiaries;
    },
    nameField(item) {
      if (this.beneficiaryIsPerson(item)) {
        return "globals.firstname";
      }
      return "globals.name";
    },
    beneficiaryIsPerson(item) {
      if (
        item &&
        item.BeneficiaryType &&
        item.BeneficiaryType.Name == "Person"
      ) {
        return true;
      }
      return false;
    },
    saveEnabled() {
      console.log(this.validTrustees);
      console.log(!(this.underageBeneficiaries().length > 0));
      return (
        this.provider &&
        this.provider.items.length > 0 &&
        this.rootValid &&
        (this.validTrustees || !(this.underageBeneficiaries().length > 0))
      );
    },
    initializeData(previousBeneficiaryPayload) {
      console.log("initialize data: ", previousBeneficiaryPayload);
      this.innerPolicyIdentities = JSON.parse(
        JSON.stringify(this.policyIdentities)
      );
      this.beneficiaryRelationships = this.columnsDropDownOptions.Models.BeneficiaryRelationship;
      this.beneficiaryTypes = this.columnsDropDownOptions.Models.BeneficiaryType;
      this.genders = this.columnsDropDownOptions.Models.Gender;
      if (
        previousBeneficiaryPayload !== null &&
        previousBeneficiaryPayload !== undefined
      ) {
        this.previousDeclarationDate =
          previousBeneficiaryPayload.DeclarationDate;
        this.beneficiaryPayload = {};
        this.beneficiaryPayload.Id = previousBeneficiaryPayload.Id;
        this.beneficiaryPayload.DeclarationDate = this.previousDeclarationDate;
        this.beneficiaryPayload.PolicyIdentities = JSON.parse(
          JSON.stringify(previousBeneficiaryPayload.PolicyIdentities)
        );
        if (
          this.beneficiaryPayload.PolicyIdentities &&
          this.beneficiaryPayload.PolicyIdentities.length > 0
        ) {
          for (
            let i = 0;
            i < this.beneficiaryPayload.PolicyIdentities.length;
            i++
          ) {
            const policyIdentity = this.beneficiaryPayload.PolicyIdentities[i];
            if (
              policyIdentity.Current !== null &&
              policyIdentity.Current !== undefined
            ) {
              policyIdentity.Name = policyIdentity.Current.Number;
              delete policyIdentity.Current;
            }
            this.innerPolicyIdentities.push(policyIdentity);
          }
        }
        this.beneficiaryPayload.Beneficiaries = JSON.parse(
          JSON.stringify(previousBeneficiaryPayload.Beneficiaries)
        );
        this.beneficiaryPayload.Beneficiaries.map((b) => {
          if (b.Id !== null && b.Id !== undefined) {
            b["UniqueIdentifier"] = b.Id;
          }
          b["Percentage"] = b["Percentage"] * 100;
          return b;
        });
        const headers = getInMemoryBeneficiariesHeaders(
          this.selectedPlan.PlanType
        );
        this.provider = new InMemoryProvider(
          this.beneficiaryPayload.Beneficiaries,
          headers
        );
        this.$nextTick(() => {
          if (this.$refs.beneficiaryTable) {
            this.$refs.beneficiaryTable.getDataAsync();
          }
          this.$emit("valid", this.saveEnabled());
        });
        // var self = this;
        // setTimeout(function() {
        //   if (self.$refs.beneficiaryTable) {
        //     self.$refs.beneficiaryTable.getDataAsync();
        //   }
        // }, 10);
      }
      if (this.innerPolicyIdentities.length === 1) {
        this.beneficiaryPayload.PolicyIdentities = [
          this.innerPolicyIdentities[0],
        ];
      }
      if (
        // this.planIdentity.UseDocumentManagement &&
        this.beneficiaryStatements !== null &&
        this.beneficiaryStatements !== undefined &&
        this.beneficiaryStatements.length > 0 &&
        this.beneficiaryStatements[0].DeclarationForm
      ) {
        for (const fileSegment of this.beneficiaryStatements[0].DeclarationForm
          .FileSegments) {
          this.filesIds.push(fileSegment.PhysicalFileId);
        }
        this.compareDates = true;
      }
      if (
        this.newMember &&
        this.beneficiaryPayload.DeclarationForm !== null &&
        this.beneficiaryPayload.DeclarationForm !== undefined
      ) {
        for (const fileSegment of this.beneficiaryPayload.DeclarationForm
          .FileSegments) {
          this.filesIds.push(fileSegment.PhysicalFileId);
        }
      }
      // else if (
      //   this.planIdentity.UseDocumentManagement &&
      //   this.beneficiaryPayload !== null &&
      //   this.beneficiaryPayload !== undefined &&
      //   this.beneficiaryPayload.DeclarationForm
      // ) {
      //   console.log("edw eisai2222!")
      //   for (const fileSegment of this.beneficiaryPayload.DeclarationForm
      //     .FileSegments) {
      //     this.filesIds.push(fileSegment.PhysicalFileId);
      //   }
      //   this.compareDates = true;
      // }
    },
    fixIds(item) {
      if (item == null) {
        return;
      }
      if (item && item.copyFrom) {
        delete item.copyFrom;
      }
      if (item && item.Gender) {
        item.GenderId = item.Gender.Id;
        delete item.Gender;
      }
      if (item && item.BeneficiaryRelationship) {
        item.BeneficiaryRelationshipId = item.BeneficiaryRelationship.Id;
        delete item.BeneficiaryRelationship;
      }
      if (item && item.BeneficiaryType) {
        item.BeneficiaryTypeId = item.BeneficiaryType.Id;
        delete item.BeneficiaryType;
      }
      if (item.Title) {
        item.TitleId = item.Title.Id;
        delete item.Title;
      }
      if (item.Suffix) {
        item.SuffixId = item.Suffix.Id;
        delete item.Suffix;
      }
      item.MemberIdentityId = this.memberIdentityId;
      delete item.MemberIdentity;
    },
    percentages(primary, payload) {
      var beneficiaries = payload.Beneficiaries.filter((b) =>
        primary ? b.IsPrimary : !b.IsPrimary
      );
      if (beneficiaries.length == 0) {
        return null;
      }
      var currentPercentage = beneficiaries.reduce((acc, beneficiary) => {
        return parseFloat((acc + beneficiary.Percentage).toFixed(4));
      }, 0);
      return currentPercentage;
    },
    // async trySubmitBeneficiaries() {
    //   if (this.hasUnrevocable) {
    //     this.dialogs.unrevocable = true;
    //   } else {
    //     await this.submitBeneficiaries();
    //   }
    // },
    async tryDeleteStatement() {
      var options = {
        planIdentityId: this.selectedPlan.Id,
        statementId: this.beneficiaryPayload.Id,
      };
      this.$emit("delete-statement", options);
      // await this.deleteBeneficiaryStatement(options);
      // this.$emit("submitted");
    },
    // async submitBeneficiaries() {
    //   this.saving = true;
    //   try {
    //     var nItem = JSON.parse(JSON.stringify(this.beneficiaryPayload));
    //     nItem.Beneficiaries.map((b) => {
    //       b.Percentage = parseFloat(b.Percentage);
    //       b.Percentage = b.Percentage / 100;
    //       return b;
    //     });
    //     var primaryPercentages = this.percentages(true, nItem);
    //     if (primaryPercentages != 1 && primaryPercentages != null) {
    //       this.error = {
    //         customMessage: "Total primary beneficiary percentage must be 100%",
    //       };
    //       return false;
    //     }
    //     var nonPrimaryPercentages = this.percentages(false, nItem);
    //     if (nonPrimaryPercentages != 1 && nonPrimaryPercentages != null) {
    //       this.error = {
    //         customMessage:
    //           "Total contingent beneficiary percentage must be 100%",
    //       };
    //       return false;
    //     }
    //     nItem.memberIdentityId = this.memberIdentityId;
    //     delete nItem.copyFrom;
    //     nItem.Beneficiaries.forEach((b) => {
    //       if (b.AddTrustee == false) {
    //         b.Trustee = null;
    //       }
    //       this.fixIds(b);
    //       this.fixIds(b.Trustee);
    //     });
    //     nItem.PlanInsuranceCarriers.forEach((p) => {
    //       if (p.PolicyIdentity) {
    //         delete p.PolicyIdentity;
    //       }
    //     });
    //     var options = {
    //       planIdentityId: this.selectedPlan.Id,
    //       payload: nItem,
    //     };
    //     await this.saveBeneficiaries(options);
    //     this.$emit("submitted");
    //   } catch (e) {
    //     console.log(e);
    //     this.$emit("submitted", e);
    //   } finally {
    //     if (this.$refs.beneficiaryTable) {
    //       this.$refs.beneficiaryTable.getDataAsync();
    //     }
    //     if (this.$refs.trusteeEditForm) {
    //       this.$refs.trusteeEditForm.resetValidation();
    //     }
    //     if (this.$refs.beneficiaryEditForm) {
    //       this.$refs.beneficiaryEditForm.resetValidation();
    //     }
    //     this.saving = false;
    //   }
    // },
    hasUnrevocable() {
      return (
        this.beneficiaryPayload.Beneficiaries.filter((b) => !b.IsRevocable)
          .length > 0
      );
    },
    trans(item, field) {
      return translate2(item, field);
    },
    allowedBeneficiaryDates(lastDeclarationDate, val) {
      if (lastDeclarationDate === null || lastDeclarationDate === "") {
        return true;
      }
      const n = Date.parse(val);
      var parsed = Date.parse(lastDeclarationDate);
      return n >= parsed;
    },
    setData(items) {
      //console.log("beneficiary set data: ", items);
    },
    async tryDelete() {
      let beneficiary = this.provider.items.filter(
        (o) => o.UniqueIdentifier === this.editedItem.UniqueIdentifier
      )[0];
      this.provider.items.splice(this.provider.items.indexOf(beneficiary), 1);
      this.dialogs.delete = false;
      this.$refs.beneficiaryTable.getDataAsync();
    },
    async trySaveBeneficiary() {
      if (this.isNew) {
        this.provider.items.push(this.editedItem);
      } else {
        var oldItem = this.provider.items.filter(
          (o) => o.UniqueIdentifier === this.editedItem.UniqueIdentifier
        )[0];
        this.provider.items[
          this.provider.items.indexOf(oldItem)
        ] = this.editedItem;
      }
      this.beneficiaryPayload.Beneficiaries = JSON.parse(
        JSON.stringify(this.provider.items)
      );
      this.$refs.beneficiaryEditForm.resetValidation();
      this.dialogs.edit = false;
      this.$refs.beneficiaryTable.getDataAsync();
      this.$emit("changed", this.provider.items);
      this.$emit("valid", this.saveEnabled());
    },
  },
};
</script>
